import React, { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { Box } from '@mui/material';
import { Button } from '@studykik/ui-library';
import { useTranslation } from 'react-i18next';
import {
  es,
  de,
  bg,
  cs,
  et,
  fr,
  el,
  sl,
  ru,
  lt,
  hr,
  bs,
  gu,
  hi,
  ka,
  kn,
  ro,
  sr,
  ta,
  te,
  uk,
  mk,
} from 'date-fns/locale';

import {
  PaymentSubmission,
  getStatusData,
} from '../../../models/paymentSubmissions';
import { environment } from '../../../environments/environment';

import './styles.scss';
import formatCurrency from '../../../utils/formatCurrency';
import { ExpenseCategoryIcons } from '../../../utils/expenses';

const locales: Record<string, Locale> = {
  es,
  de,
  bg,
  cs,
  et,
  fr,
  el,
  sl,
  ru,
  lt,
  hr,
  bs,
  gu,
  hi,
  ka,
  kn,
  ro,
  sr,
  ta,
  te,
  uk,
  mk,
};

export interface PaymentSubmissionsTableProps {
  paymentSubmissions: PaymentSubmission[];
}

export const PaymentSubmissionsTable: FC<PaymentSubmissionsTableProps> = (
  props
) => {
  const { paymentSubmissions } = props;

  const { t, i18n } = useTranslation();

  const renderRow = (row: PaymentSubmission) => {
    const {
      id,
      status,
      paymentSubcategory,
      processorRewardLink,
      amount,
      paymentCategory,
      expenseCategoryName,
      expenseCategoryNameTranslation,
    } = row;
    const statusData = getStatusData(status);

    const loadExternalRewardLink = () => {
      if (processorRewardLink) {
        window.open(processorRewardLink, '_blank');
      }
    };

    const getAction = () => {
      switch (statusData.key) {
        case 'approved':
          return (
            processorRewardLink && (
              <div className="transactionNameCellAction">
                <Button
                  type="button"
                  onClick={loadExternalRewardLink}
                  variant="outlined"
                  className="claim-funds-button"
                >
                  <img
                    src={`${environment.cdnUrl}/images/icon-gift.svg`}
                    alt={t('Claim reward icon')}
                  />
                  {t('Claim Reward')}
                </Button>
              </div>
            )
          );
        case 'failed':
        case 'denied':
          return null;
        default:
          break;
      }
      return <></>;
    };

    return (
      <Box
        key={`${paymentSubcategory}${id}`}
        className="request-row"
        display="flex"
        justifyContent="space-between"
      >
        <div className="transactionNameCell">
          <div className="transactionIcon" data-testid="expense-icon">
            {expenseCategoryName
              ? ExpenseCategoryIcons[expenseCategoryName]
              : ExpenseCategoryIcons.Other}
          </div>
          <div className="transactionNameCellData">
            <div className="transactionName">{paymentSubcategory}</div>
            <div className="transactionDate">
              {expenseCategoryNameTranslation || expenseCategoryName || paymentCategory}
            </div>
          </div>
          {getAction()}
        </div>
        <div className="transactionNameCell status-cell">
          <div className="amount">
            {formatCurrency({ amount, currency: row.currency })}
          </div>
          <div
            className={['status-chip', statusData.key, i18n?.options?.lng].join(
              ' '
            )}
          >
            {t(statusData.label)}
          </div>
        </div>
      </Box>
    );
  };

  const renderDateRow = (date: string) => (
    <div key={date}>
      <div className="tb-cell transparent">
        <div>
          {format(new Date(date), 'LLL d', {
            locale: locales[i18n?.options?.lng || ''],
          })}
        </div>
      </div>
    </div>
  );

  const rows: (string | PaymentSubmission)[] = useMemo(() => {
    const paymentSubmissionsByDate = paymentSubmissions.reduce(
      (obj: Record<string, (string | PaymentSubmission)[]>, submission) => {
        const createdAtDate = submission.submitDate.substring(0, 10);
        if (obj[createdAtDate]) {
          obj[createdAtDate].push(submission);
        } else {
          // eslint-disable-next-line no-param-reassign
          obj[createdAtDate] = [createdAtDate, submission];
        }
        return obj;
      },
      {} as Record<string, (string | PaymentSubmission)[]>
    );
    return Object.values(paymentSubmissionsByDate).flat();
  }, [paymentSubmissions]);

  return (
    <div className="transactions-table-wrapper">
      <div className="transactions-table">
        {rows.map((rowItem) => {
          if (typeof rowItem === 'string') {
            return renderDateRow(rowItem);
          }

          return renderRow(rowItem);
        })}
      </div>
    </div>
  );
};
